/* AppTopbar — concrete topbar used by non-forecast routes (Phase A2).
 *
 * Composes the v2 <Topbar> primitive with:
 *   - left: page title derived from useLocation() (bilingual via useLang)
 *   - right: ViewerChip
 *
 * Scope decisions (current):
 *   - Theme toggle (Phase B2) — wired here, shared via lib/theme.ts so
 *     ForecastItaly's own slider and this button stay in sync.
 *   - Lang toggle (Phase B3) — IT/EN segmented wired to setLang() from
 *     lib/lang.ts. ForecastItaly subscribes to the broadcast and keeps
 *     its in-memory state.lang in sync (the local Segmented in its head
 *     still works as before — both setters drive the same persisted slot).
 *   - This topbar is suppressed on /forecast/italy and /market/* (the
 *     ForecastItaly routes) because their bespoke `.head` already renders
 *     a richer top bar with refresh status, KPI sticky stat, value-mode
 *     segmented, scenarios, timeframe — none of which belong in a global
 *     topbar.
 *
 * The title-from-route table mirrors AppSidebar's currentId derivation —
 * keep them in sync when a new route lands.
 */
import React from "react";
import { useLocation } from "react-router-dom";
import Topbar from "./Topbar";
import { useLang } from "../../lib/lang";
import { useTheme, type Theme } from "../../lib/theme";
import ViewerChip from "../composed/ViewerChip";
// PR 2 — lang now lives in a dropdown menu (was IT|EN segmented). Better
// fit for the topbar visually, and scales when more languages are added.
import LangMenu from "./LangMenu";

function useRouteTitle(): string {
  const lang = useLang();
  const { pathname } = useLocation();

  if (pathname.startsWith("/admin/users")) {
    return lang === "it" ? "Utenti" : "Users";
  }
  if (pathname.startsWith("/admin/audit")) {
    return lang === "it" ? "Registro modifiche" : "Audit log";
  }
  if (pathname === "/me") {
    return lang === "it" ? "Il mio profilo" : "My profile";
  }
  if (pathname.startsWith("/u/")) {
    // The dynamic name shows inside the page body; keep the topbar generic
    // so the title is stable across re-renders / loading states.
    return lang === "it" ? "Profilo" : "Profile";
  }
  if (pathname.startsWith("/map")) {
    return lang === "it" ? "Mappa della piattaforma" : "Platform map";
  }
  // 404 catch-all
  return lang === "it" ? "Pagina non trovata" : "Page not found";
}

/** Sun/moon icon button. Click flips theme. Inherits currentColor so
 *  the same SVG looks right in both light and dark modes. Matches the
 *  vendored Lucide-style icons used in AppSidebar (consistent stroke
 *  width + size across the shell). */
function ThemeButton() {
  const [theme, setTheme] = useTheme();
  const lang = useLang();
  const isDark = theme === "dark";
  const next: Theme = isDark ? "light" : "dark";
  const aria = lang === "it"
    ? (isDark ? "Passa a tema chiaro" : "Passa a tema scuro")
    : (isDark ? "Switch to light theme" : "Switch to dark theme");
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={aria}
      aria-pressed={isDark}
      className="app-topbar__icon-btn"
    >
      {isDark ? (
        // Sun — click to go light
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <line x1="12" y1="2"  x2="12" y2="5"/>
          <line x1="12" y1="19" x2="12" y2="22"/>
          <line x1="2"  y1="12" x2="5"  y2="12"/>
          <line x1="19" y1="12" x2="22" y2="12"/>
          <line x1="4.5"  y1="4.5"  x2="6.5"  y2="6.5"/>
          <line x1="17.5" y1="17.5" x2="19.5" y2="19.5"/>
          <line x1="4.5"  y1="19.5" x2="6.5"  y2="17.5"/>
          <line x1="17.5" y1="6.5"  x2="19.5" y2="4.5"/>
        </svg>
      ) : (
        // Moon — click to go dark
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.5A9 9 0 1 1 11.5 3a7 7 0 0 0 9.5 9.5z" />
        </svg>
      )}
    </button>
  );
}

export default function AppTopbar() {
  const title = useRouteTitle();
  return (
    <Topbar
      left={<span className="app-topbar__title">{title}</span>}
      center={null}
      right={
        <>
          <LangMenu />
          <ThemeButton />
          <ViewerChip compact />
        </>
      }
    />
  );
}
